import { useState } from "react";
import { Container, Form, Button } from "react-bootstrap";
import { Eye, EyeOff, Mail, Lock, Shield } from "lucide-react";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      console.log('Attempting login with:', { email });
      
      const tokenResponse = await axios.post('https://backend.dlapakistan.com/api/token/', {
        email: email,
        password: password
      });

      console.log('Token response:', tokenResponse.data);

      // Create user data directly - no extra API call needed
      const userData = {
        id: 1,
        email: email,
        username: email.split('@')[0],
        is_active: true
      };

      login({
        access: tokenResponse.data.access,
        refresh: tokenResponse.data.refresh,
        user: userData
      });

      Swal.fire({
        title: 'Success!',
        text: 'Logged in successfully!',
        icon: 'success',
        confirmButtonColor: '#059669',
        timer: 1500,
        showConfirmButton: false
      }).then(() => {
        navigate('/home');
      });

    } catch (err) {
      console.error('Login error:', err.response || err.message);
      
      if (err.response?.status === 404 || err.response?.status === 405) {
        try {
          console.log('Trying alternative login endpoint...');
          const response = await axios.post('https://backend.dlapakistan.com/api/auth/login/', {
            email: email,
            password: password
          });
          
          localStorage.setItem('access_token', response.data.access || response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user || { email }));
          
          Swal.fire({
            title: 'Success!',
            text: 'Logged in successfully!',
            icon: 'success',
            confirmButtonColor: '#059669',
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            navigate('/home');
          });
          return;
        } catch (secondError) {
          console.error('Second login attempt failed:', secondError);
        }
      }

      let errorMessage = 'Login failed. Please try again.';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.non_field_errors) {
        errorMessage = err.response.data.non_field_errors[0];
      } else if (err.message === 'Network Error') {
        errorMessage = 'Cannot connect to server.';
      }
      
      setError(errorMessage);
      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <Container className="d-flex justify-content-center">
        <div 
          className="bg-white w-full max-w-md rounded-xl p-8 shadow-[8px_0_25px_-5px_rgba(0,0,0,0.2),12px_0_30px_-8px_rgba(0,0,0,0.15)] border-r border-gray-200"
          style={{backgroundColor: '#ffffff'}}
        >
          
          {/* Header Logo - Same as Sidebar */}
          <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Shield className="text-white w-7 h-7" />
              </div>
              <div className="text-left">
                <h1 className="text-gray-800 font-bold text-2xl drop-shadow-sm">ADMIN PANEL</h1>
                <p className="text-emerald-600 text-sm drop-shadow-sm">Admin Login</p>
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-600 text-sm">
              Enter your credentials to access your account
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm shadow-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <Form onSubmit={handleSubmit}>
            {/* Email Field */}
            <Form.Group className="mb-4">
              <Form.Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Mail size={16} className="mr-2 text-gray-500" />
                Email Address
              </Form.Label>
              <div className="relative">
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@admin.com"
                  required
                  className="py-3 pl-10 pr-4 rounded-lg border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 transition-all duration-200 w-full shadow-sm"
                  style={{backgroundColor: '#ffffff'}}
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Mail size={18} />
                </div>
              </div>
            </Form.Group>

            {/* Password Field */}
            <Form.Group className="mb-6">
              <Form.Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Lock size={16} className="mr-2 text-gray-500" />
                Password
              </Form.Label>
              <div className="relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="py-3 pl-10 pr-12 rounded-lg border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 transition-all duration-200 w-full shadow-sm"
                  style={{backgroundColor: '#ffffff'}}
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Lock size={18} />
                </div>
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="flex justify-end mt-2">
                <a
                  href="#forgot-password"
                  className="text-sm text-emerald-600 hover:text-emerald-800 font-medium transition-colors"
                >
                  Forgot password?
                </a>
              </div>
            </Form.Group>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg border-0 bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-600/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-2 align-[-2px]"></span>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </Form>

          {/* Footer Note */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Login;